import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Checkbox, Button, notification, Row, Col, Typography, Space, Divider } from 'antd';
import {
    BankOutlined,
    MailOutlined,
    PhoneOutlined,
    IdcardOutlined,
    EnvironmentOutlined,
    SafetyCertificateOutlined,
    CheckCircleFilled,
    InfoCircleOutlined,
    WarningFilled,
    ArrowRightOutlined
} from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { setUserProfile } from '../../../../Reducers/userProfileReducer';
import { completeProfile } from '../../../../Constants/Api/functions/function';
import './style.scss';

const { Title, Text, Paragraph } = Typography;

const CompleteProfileModal = ({ open, onCancel, onCompleted }) => {
    const [form] = Form.useForm();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isNotEmployed, setIsNotEmployed] = useState(false);
    const userProfile = useSelector(state => state.userProfile?.userProfile);
    const dispatch = useDispatch();

    useEffect(() => {
        if (open && userProfile) {
            const notEmp = userProfile.isEmployed === 0 || userProfile.isEmployed === false;
            setIsNotEmployed(notEmp);
            form.setFieldsValue({
                notEmployed: notEmp,
                companyName: userProfile.companyName || '',
                companyEmail: userProfile.companyEmail || '',
                companyMobile: userProfile.companyMobile || '',
                companyDesignation: userProfile.companyDesignation || '',
                companyAddress: userProfile.companyAddress || '',
            });
        }
    }, [open, userProfile, form]);

    const handleNotEmployedChange = (e) => {
        const checked = e.target.checked;
        setIsNotEmployed(checked);
        if (checked) {
            form.setFieldsValue({
                companyName: '',
                companyEmail: '',
                companyMobile: '',
                companyDesignation: '',
                companyAddress: '',
            });
        }
    };

    const handleFinish = async (values) => {
        if (!userProfile?.id) return;

        if (!values.notEmployed) {
            if (
                !values.companyName?.trim() ||
                !values.companyEmail?.trim() ||
                !values.companyDesignation?.trim() ||
                !values.companyMobile?.trim() ||
                !values.companyAddress?.trim()
            ) {
                notification.warning({
                    message: 'Required Fields Missing',
                    description: 'Please fill in all 5 company fields (Company Name, Email, Designation, Mobile, and Address) before submitting.',
                    placement: 'topRight',
                });
                return;
            }
        }

        setIsSubmitting(true);
        try {
            const payload = {
                userId: userProfile.id,
                isEmployed: !values.notEmployed,
                companyName: values.notEmployed ? null : values.companyName?.trim(),
                companyEmail: values.notEmployed ? null : values.companyEmail?.trim()?.toLowerCase(),
                companyMobile: values.notEmployed ? null : values.companyMobile?.trim(),
                companyDesignation: values.notEmployed ? null : values.companyDesignation?.trim(),
                companyAddress: values.notEmployed ? null : values.companyAddress?.trim(),
            };

            const response = await completeProfile(payload);

            if (response && (response.status === 200 || response.data?.success)) {
                const updatedUser = response.data?.user || {
                    ...userProfile,
                    ...payload,
                    isProfileCompleted: 1
                };

                // Update Redux state and localStorage
                dispatch(setUserProfile(updatedUser));
                localStorage.setItem('digital-academy-user', JSON.stringify(updatedUser));
                sessionStorage.removeItem('profile_completion_dismissed');

                notification.success({
                    message: 'Profile Completed',
                    description: 'Your employment profile details have been saved successfully.',
                    icon: <CheckCircleFilled style={{ color: '#52c41a' }} />,
                    placement: 'topRight',
                });

                if (onCompleted) {
                    onCompleted(updatedUser);
                }
            } else {
                const msg = response?.data?.message || 'Failed to update profile details. Please try again.';
                notification.error({
                    message: 'Update Failed',
                    description: msg,
                    placement: 'topRight',
                });
            }
        } catch (error) {
            console.error('completeProfile error:', error);
            notification.error({
                message: 'Error',
                description: 'An unexpected error occurred while saving your details.',
                placement: 'topRight',
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Modal
            open={open}
            onCancel={onCancel}
            footer={null}
            centered
            width={640}
            className="complete-profile-modal"
            maskClosable={true}
            destroyOnClose={false}
        >
            <div className="complete-profile-header">
                <div className="badge-icon-wrap">
                    <SafetyCertificateOutlined className="shield-icon" />
                </div>
                <div className="header-text">
                    <Title level={3} className="title">Complete Your Profile</Title>
                    <Paragraph className="subtitle">
                        Please provide your current organization and workplace details. This information is required for student verification and official certifications.
                    </Paragraph>
                </div>
            </div>

            <div className="student-info-strip">
                <div className="strip-item">
                    <span className="label">Student:</span>
                    <span className="value">{userProfile?.name || 'N/A'}</span>
                </div>
                <div className="strip-item">
                    <span className="label">Student Email:</span>
                    <span className="value">{userProfile?.email || 'N/A'}</span>
                </div>
                {userProfile?.contactNumber && (
                    <div className="strip-item">
                        <span className="label">Mobile:</span>
                        <span className="value">{userProfile.contactNumber}</span>
                    </div>
                )}
            </div>

            <Form
                form={form}
                layout="vertical"
                onFinish={handleFinish}
                className="complete-profile-form"
                requiredMark={true}
            >
                <div className="employment-status-toggle">
                    <Form.Item name="notEmployed" valuePropName="checked" noStyle>
                        <Checkbox onChange={handleNotEmployedChange} className="employment-checkbox">
                            <span className="checkbox-label">
                                I am not currently employed / Full-time Student
                            </span>
                        </Checkbox>
                    </Form.Item>
                    <Text type="secondary" className="helper-text">
                        Check this if you are not currently working at any company or institution.
                    </Text>
                </div>

                <Divider style={{ margin: '14px 0 20px 0' }} />

                {!isNotEmployed ? (
                    <div className="company-fields">
                        <Row gutter={16}>
                            <Col xs={24} sm={12}>
                                <Form.Item
                                    name="companyName"
                                    label="Company Name"
                                    rules={[
                                        { required: true, whitespace: true, message: 'Please enter company name' },
                                        { min: 2, message: 'Company name must be at least 2 characters' }
                                    ]}
                                >
                                    <Input
                                        prefix={<BankOutlined className="field-icon" />}
                                        placeholder="e.g. Virtusa, Dialog, ABC Ltd"
                                        size="large"
                                    />
                                </Form.Item>
                            </Col>

                            <Col xs={24} sm={12}>
                                <Form.Item
                                    name="companyEmail"
                                    label="Company Mail / Work Email"
                                    rules={[
                                        { required: true, whitespace: true, message: 'Please enter company email' },
                                        { type: 'email', message: 'Please enter a valid company email address' }
                                    ]}
                                >
                                    <Input
                                        prefix={<MailOutlined className="field-icon" />}
                                        placeholder="e.g. name@company.com"
                                        size="large"
                                    />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Row gutter={16}>
                            <Col xs={24} sm={12}>
                                <Form.Item
                                    name="companyDesignation"
                                    label="Company Designation"
                                    rules={[
                                        { required: true, whitespace: true, message: 'Please enter designation' }
                                    ]}
                                >
                                    <Input
                                        prefix={<IdcardOutlined className="field-icon" />}
                                        placeholder="e.g. Software Engineer, Executive"
                                        size="large"
                                    />
                                </Form.Item>
                            </Col>

                            <Col xs={24} sm={12}>
                                <Form.Item
                                    name="companyMobile"
                                    label="Company Mobile No."
                                    rules={[
                                        { required: true, whitespace: true, message: 'Please enter company mobile number' },
                                        {
                                            pattern: /^[0-9+()-\s]{7,20}$/,
                                            message: 'Please enter a valid phone number'
                                        }
                                    ]}
                                >
                                    <Input
                                        prefix={<PhoneOutlined className="field-icon" />}
                                        placeholder="e.g. 077 123 4567 or 011 234 5678"
                                        size="large"
                                    />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Form.Item
                            name="companyAddress"
                            label="Company Address"
                            rules={[
                                { required: true, whitespace: true, message: 'Please enter company address' },
                                { min: 5, message: 'Please enter a complete address' }
                            ]}
                        >
                            <Input.TextArea
                                placeholder="e.g. No. 123, Galle Road, Colombo 03"
                                rows={2}
                                size="large"
                            />
                        </Form.Item>
                    </div>
                ) : (
                    <div className="not-employed-banner">
                        <InfoCircleOutlined className="info-icon" />
                        <div>
                            <Text strong>Registering as Full-Time Student</Text>
                            <Paragraph className="not-employed-desc">
                                Your profile will be marked as a full-time student. You can update your employment details at any time from your profile settings.
                            </Paragraph>
                        </div>
                    </div>
                )}

                <div className="form-actions">
                    <Button
                        onClick={onCancel}
                        size="large"
                        className="btn-cancel"
                        disabled={isSubmitting}
                    >
                        Remind Me Later
                    </Button>
                    <Button
                        type="primary"
                        htmlType="submit"
                        size="large"
                        loading={isSubmitting}
                        className="btn-submit"
                    >
                        Save & Complete Profile
                    </Button>
                </div>
            </Form>
        </Modal>
    );
};

export default CompleteProfileModal;
